import Logger from '../../Logging';
import { BlurRenderer } from './BlurRenderer';

const INFO = 'Utilities/Gnural/LocalMediaHandler/BlurHandler.js';
const INFERENCE_SIZE = 256;
const OUTPUT_FRAME_RATE = 30;

export class BlurHandler {
	#IsInitialized = false;
	#Renderer = null;
	#Worker = null;
	#WorkerFailed = false;
	#ReadyResolve = null;
	#ReadyReject = null;
	#DisposeResolve = null;
	#InferenceCanvas = null;
	#InferenceContext = null;
	#PendingInference = null;
	#CanvasRef = null;
	#CanvasStream = null;
	#VideoRef = null;
	#VideoTrack = null;
	#CurrentCallbackID = null;
	#ShouldProcess = false;
	#BlurAmount = 20;
	#Generation = 0;
	#VideoWidth = 0;
	#VideoHeight = 0;
	#LastTimestamp = 0;

	async Init() {
		if (this.#IsInitialized) return !this.#WorkerFailed;
		try {
			if (typeof Worker === 'undefined' || typeof createImageBitmap !== 'function' ||
                typeof OffscreenCanvas === 'undefined' ||
                typeof OffscreenCanvas.prototype.transferToImageBitmap !== 'function' ||
                typeof HTMLVideoElement.prototype.requestVideoFrameCallback !== 'function') {
				throw new Error('Background blur requires workers, OffscreenCanvas, ImageBitmap and video frame callbacks');
			}

			this.#CanvasRef = document.createElement('canvas');
			this.#Renderer = new BlurRenderer(this.#CanvasRef);
			this.#CanvasStream = this.#CanvasRef.captureStream(OUTPUT_FRAME_RATE);
			this.#InferenceCanvas = document.createElement('canvas');
			this.#InferenceCanvas.width = INFERENCE_SIZE;
			this.#InferenceCanvas.height = INFERENCE_SIZE;
			this.#InferenceContext = this.#InferenceCanvas.getContext('2d', { alpha: false });
			if (!this.#InferenceContext) throw new Error('Unable to create the segmentation input canvas');

			this.#VideoRef = document.createElement('video');
			this.#VideoRef.muted = true;
			this.#VideoRef.playsInline = true;

			// Classic worker: the MediaPipe WASM loader uses importScripts.
			this.#WorkerFailed = false;
			this.#Worker = new Worker(new URL('./BlurSegmentation.worker.js', import.meta.url));
			this.#Worker.onmessage = event => this.#OnWorkerMessage(event.data);
			this.#Worker.onerror = event => {
				event.preventDefault();
				this.#FailWorker(new Error(event.message || 'Segmentation worker failed'));
			};
			this.#Worker.onmessageerror = () => this.#FailWorker(new Error('Unable to read a segmentation worker message'));
			const delegate = await new Promise((resolve, reject) => {
				this.#ReadyResolve = resolve;
				this.#ReadyReject = reject;
				this.#Worker.postMessage({ type: 'init' });
			});
			this.#ReadyResolve = null;
			this.#ReadyReject = null;
			if (delegate === 'CPU') Logger.warn(INFO, 'GPU segmentation unavailable; using the CPU worker');
			this.#IsInitialized = true;
			return true;
		} catch (error) {
			Logger.warn(INFO, 'Failed to initialize blur handler', error);
			await this.Dispose();
			return false;
		}
	}

	#StopRendering() {
		this.#ShouldProcess = false;
		this.#VideoRef?.pause();
		if (this.#CurrentCallbackID !== null) {
			this.#VideoRef?.cancelVideoFrameCallback(this.#CurrentCallbackID);
			this.#CurrentCallbackID = null;
		}
	}

	async Stop() {
		if (!this.#IsInitialized) return false;
		this.#StopRendering();
		await this.#PendingInference?.promise;
		return true;
	}

	async Start() {
		if (!this.#IsInitialized || this.#WorkerFailed || this.#VideoTrack?.readyState !== 'live') return false;
		if (!(await this.Stop())) return false;
		try {
			this.#ResetMaskHistory();
			await this.#VideoRef.play();
			if (this.#WorkerFailed) return false;
			this.#ShouldProcess = true;
			this.#CurrentCallbackID = this.#VideoRef.requestVideoFrameCallback(() => this.#OnFrame());
			return true;
		} catch (error) {
			this.#StopRendering();
			Logger.warn(INFO, 'Failed to start blur handler', error);
			return false;
		}
	}

	IsStarted() {
		return this.#ShouldProcess;
	}

	async UpdateVideoTrackSource(videoTrack) {
		if (!this.#IsInitialized) throw new Error('BlurHandler must be initialized before updating video track source');
		if (!(videoTrack instanceof MediaStreamTrack) || videoTrack.kind !== 'video') {
			throw new Error('Input must be a video MediaStreamTrack');
		}
		if (videoTrack.readyState !== 'live') throw new Error('Input video track must be in a live state');

		const restart = this.#ShouldProcess;
		await this.Stop();
		this.#VideoTrack = videoTrack;
		this.#VideoRef.srcObject = new MediaStream([videoTrack]);
		this.#ResetMaskHistory();
		if (restart && !(await this.Start())) throw new Error('Failed to resume blur after changing the video source');
	}

	SetBlurAmount(amount) {
		if (!Number.isFinite(amount) || amount < 0 || amount > 20) {
			throw new Error('Blur amount must be between 0 and 20');
		}
		this.#BlurAmount = amount;
	}

	#ResetMaskHistory() {
		this.#Generation++;
		this.#Renderer?.ResetMask();
	}

	#OnFrame() {
		this.#CurrentCallbackID = null;
		if (!this.#ShouldProcess) return;
		try {
			const width = this.#VideoRef.videoWidth;
			const height = this.#VideoRef.videoHeight;
			if (width && height) {
				if (width !== this.#VideoWidth || height !== this.#VideoHeight) {
					this.#VideoWidth = width;
					this.#VideoHeight = height;
					this.#ResetMaskHistory();
				}
				// Render the current camera frame even when inference is still working on an older one.
				this.#Renderer.Render(this.#VideoRef, this.#BlurAmount);
				if (!this.#PendingInference) this.#RequestMask();
			}
			this.#CurrentCallbackID = this.#VideoRef.requestVideoFrameCallback(() => this.#OnFrame());
		} catch (error) {
			this.#StopRendering();
			Logger.error(INFO, 'Failed to render blurred video', error);
		}
	}

	#RequestMask() {
		const request = { generation: this.#Generation, promise: null, resolve: null };
		request.promise = new Promise(resolve => { request.resolve = resolve; });
		this.#PendingInference = request;
		void this.#CaptureMask(request);
	}

	async #CaptureMask(request) {
		let bitmap = null;
		try {
			this.#InferenceContext.drawImage(this.#VideoRef, 0, 0, INFERENCE_SIZE, INFERENCE_SIZE);
			bitmap = await createImageBitmap(this.#InferenceCanvas);
			if (!this.#ShouldProcess || this.#WorkerFailed || request.generation !== this.#Generation) {
				bitmap.close();
				bitmap = null;
				this.#FinishInference(request);
				return;
			}
			// Keep VIDEO timestamps increasing across source replacements and restarts.
			const timestamp = Math.max(performance.now(), this.#LastTimestamp + 0.001);
			this.#LastTimestamp = timestamp;
			this.#Worker.postMessage({ type: 'frame', bitmap, timestamp, generation: request.generation }, [bitmap]);
			bitmap = null;
		} catch (error) {
			bitmap?.close();
			this.#FinishInference(request);
			Logger.warn(INFO, 'Failed to capture a segmentation frame', error);
		}
	}

	#FinishInference(request = this.#PendingInference) {
		if (!request) return;
		if (this.#PendingInference === request) this.#PendingInference = null;
		request.resolve();
	}

	#OnWorkerMessage(data) {
		if (data.type === 'ready') {
			this.#ReadyResolve?.(data.delegate);
		} else if (data.type === 'mask' || data.type === 'frame-error') {
			const request = this.#PendingInference;
			try {
				if (data.type === 'frame-error') {
					Logger.warn(INFO, 'Failed to segment a video frame', data.message);
				} else if (this.#ShouldProcess && data.generation === this.#Generation) {
					this.#Renderer.UpdateMask(data);
				} else {
					data.bitmap?.close();
				}
			} catch (error) {
				this.#StopRendering();
				Logger.error(INFO, 'Failed to update the foreground mask', error);
			} finally {
				if (request?.generation === data.generation) this.#FinishInference(request);
			}
		} else if (data.type === 'disposed') {
			this.#DisposeResolve?.();
		} else if (data.type === 'error') {
			this.#FailWorker(new Error(data.message));
		}
	}

	#FailWorker(error) {
		this.#WorkerFailed = true;
		this.#StopRendering();
		this.#ReadyReject?.(error);
		this.#FinishInference();
		this.#DisposeResolve?.();
		this.#Worker?.terminate();
		Logger.error(INFO, 'Segmentation worker failed', error);
	}

	GetBlurredVideoTrack() {
		return this.#CanvasStream?.getVideoTracks()[0] ?? null;
	}

	GetOriginalVideoTrack() {
		return this.#VideoTrack ?? null;
	}

	async Dispose() {
		this.#StopRendering();
		await this.#PendingInference?.promise;
		try {
			if (this.#Worker && !this.#WorkerFailed) {
				await new Promise(resolve => {
					this.#DisposeResolve = resolve;
					this.#Worker.postMessage({ type: 'dispose' });
				});
			}
		} catch (error) {
			Logger.warn(INFO, 'Failed to close the segmentation worker cleanly', error);
		} finally {
			this.#Worker?.terminate();
			this.#Worker = null;
			this.#ReadyResolve = null;
			this.#ReadyReject = null;
			this.#DisposeResolve = null;
			this.#CanvasStream?.getTracks().forEach(track => track.stop());
			if (this.#VideoRef) this.#VideoRef.srcObject = null;
			this.#Renderer?.Dispose();
			this.#Renderer = null;
			this.#VideoRef = null;
			this.#VideoTrack = null;
			this.#CanvasStream = null;
			this.#CanvasRef = null;
			this.#InferenceContext = null;
			this.#InferenceCanvas = null;
			this.#VideoWidth = 0;
			this.#VideoHeight = 0;
			this.#LastTimestamp = 0;
			this.#Generation++;
			this.#IsInitialized = false;
			this.#WorkerFailed = false;
		}
	}
}
